import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import {
  useInitiateGoogleDriveAuth,
  useGetGoogleDriveConnectionStatus,
  useDeleteGoogleDriveConnection,
} from '@/shared/api';

export const useGDriveConnection = () => {
  const { accessToken } = useAuth();
  const { showNotification } = useNotificationContext();
  const queryClient = useQueryClient();

  const initiateGoogleDriveAuthMutation = useInitiateGoogleDriveAuth<{
    authUrl: string;
  }>({});
  const deleteGoogleDriveConnectionMutation = useDeleteGoogleDriveConnection();
  const { data, isFetching: gDriveIsLoading } =
    useGetGoogleDriveConnectionStatus<{ connected: boolean }>({
      queryParams: { serviceType: 'google' },
    });
  const gDriveConnected = Boolean(data?.connected);

  const onGDriveDisconnect = async () => {
    try {
      await deleteGoogleDriveConnectionMutation.mutateAsync({
        queryParams: { serviceType: 'google' },
      });
      queryClient.invalidateQueries(['google-drive', 'connection-status']);
      showNotification({
        type: 'success',
        message: 'Success',
        description: 'Disconnected from Google Drive successfully',
      });
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Error',
        description: 'Failed to disconnect from Google Drive',
      });
    }
  };

  const onGDriveConnect = async () => {
    try {
      const { data } = await initiateGoogleDriveAuthMutation.refetch();
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Error',
        description: 'Failed to connect to Google Drive',
      });
    }
  };

  useEffect(() => {
    queryClient.invalidateQueries(['google-drive', 'connection-status']);
  }, [accessToken]);

  return {
    gDriveConnected,
    gDriveIsLoading,
    onGDriveDisconnect,
    onGDriveConnect,
  };
};
